import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthHTTPService } from '../../features/auth/services/auth-http.service';
import { CustomError } from '../models/CustomError';
import { NO_AUTHORIZATION } from '../helpers/metadataInterceptor';
import { LoginResponse } from '../../features/auth/models/LoginResponse';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    const router = inject(Router);
    const authHTTPService = inject(AuthHTTPService);

    if (req.context.get(NO_AUTHORIZATION)) {
        return next(req);
    }

    const token = authHTTPService.getAccessTokenLocalStorage();
    if (token) {
        req = addToken(req, token);
    }

    return next(req).pipe(
        catchError((error) => {
            if (
                error instanceof HttpErrorResponse &&
                error.url &&
                (error.url.includes('/auth/refresh') ||
                    (error?.error?.errors as Array<CustomError>)?.some(
                        (e) => e.error_code == 'authentication.token.errors.invalid',
                    ))
            ) {
                isRefreshing = false;
                router.navigateByUrl('/auth/login?session-expired=true');
                return throwError(() => error);
            }
            if (error instanceof HttpErrorResponse && error.status === 401 && !error.url.includes('/auth/refresh')) {
                return handle401Error(req, next, authHTTPService);
            }
            return throwError(() => error);
        }),
    );
};

function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    const authHeader = `Bearer ${token}`;
    return request.clone({ setHeaders: { Authorization: authHeader } });
}

function handle401Error(request: HttpRequest<unknown>, next: HttpHandlerFn, authHTTPService: AuthHTTPService) {
    if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        return authHTTPService.refreshToken().pipe(
            switchMap((token: LoginResponse) => {
                isRefreshing = false;
                refreshTokenSubject.next(token.access_token);
                return next(addToken(request, token.access_token));
            }),
        );
    }
    return refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => next(addToken(request, jwt!))),
    );
}
